// FILE: src/components/BookingModal.js

import React, { useState } from 'react';
import { X, Clock, Users, Calendar, UtensilsCrossed } from 'lucide-react';

export default function BookingModal({ restaurant, voucher, onClose, user }) {
    const [bookingDetails, setBookingDetails] = useState({
        date: new Date().toISOString().split('T')[0],
        time: '19:00',
        guests: 2,
    });
    const [isBooked, setIsBooked] = useState(false);
    const [isBooking, setIsBooking] = useState(false);
    const [error, setError] = useState('');

    if (!restaurant || !voucher) return null;

    const handleInputChange = (e) => setBookingDetails(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsBooking(true);

        try {
            // The user object from Firebase Auth might contain more than we need.
            // Let's create a simpler object for the request body.
            const userPayload = user && !user.isAnonymous 
                ? { uid: user.uid, email: user.email }
                : { uid: 'anonymous' };

            const response = await fetch('/api/book', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    restaurantId: restaurant.id,
                    restaurantName: restaurant.name,
                    ownerId: restaurant.ownerId,
                    voucher,
                    ...bookingDetails,
                    bookedBy: userPayload,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Booking failed. Please try again.');
            }

            setIsBooked(true);
        } catch (err) {
            console.error("Error creating booking:", err);
            setError(err.message);
        } finally {
            setIsBooking(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full relative animate-fade-in-up">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><X size={28} /></button>
                {isBooked ? (
                    <div className="text-center py-8">
                        <div className="mx-auto bg-green-100 rounded-full h-20 w-20 flex items-center justify-center mb-4"><UtensilsCrossed className="h-10 w-10 text-green-600" /></div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-2">Booking Confirmed!</h2>
                        <p className="text-gray-600">Your table at <span className="font-semibold text-gray-900">{restaurant.name}</span> is booked.</p>
                        <button onClick={onClose} className="mt-8 w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700">Done</button>
                    </div>
                ) : (
                    <>
                        <h2 className="text-3xl font-extrabold text-gray-900 mb-1">Book a Table</h2>
                        <p className="text-gray-600 mb-4">at <span className="font-semibold">{restaurant.name}</span></p>
                        <div className="bg-teal-50 border-l-4 border-teal-400 text-teal-800 p-4 rounded-r-lg my-5">
                            <p className="font-bold">Voucher Applied: "{voucher.description}"</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="guests" className="block text-sm font-medium text-gray-700">Guests</label>
                                <div className="mt-1 relative"><Users className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" /><input type="number" name="guests" id="guests" min="1" max="10" value={bookingDetails.guests} onChange={handleInputChange} className="block w-full rounded-lg border-gray-300 pl-10 py-2.5" required/></div>
                            </div>
                            <div>
                                <label htmlFor="date" className="block text-sm font-medium text-gray-700">Date</label>
                                <div className="mt-1 relative"><Calendar className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" /><input type="date" name="date" id="date" value={bookingDetails.date} onChange={handleInputChange} className="block w-full rounded-lg border-gray-300 pl-10 py-2.5" required/></div>
                            </div>
                            <div>
                                <label htmlFor="time" className="block text-sm font-medium text-gray-700">Time</label>
                                <div className="mt-1 relative"><Clock className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" /><input type="time" name="time" id="time" value={bookingDetails.time} onChange={handleInputChange} className="block w-full rounded-lg border-gray-300 pl-10 py-2.5" required/></div>
                            </div>
                            {error && <p className="text-red-500 text-sm">{error}</p>}
                            <button type="submit" disabled={isBooking} className="w-full bg-blue-600 text-white font-bold py-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-300">
                                {isBooking ? 'Booking...' : 'Confirm Booking'}
                            </button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
